"use client";

import { useCallback, useMemo, createContext, useContext, ReactNode, FC, useEffect } from "react";
import { AppRouterContext } from "next/dist/shared/lib/app-router-context.shared-runtime";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import { useQuery } from "@stadium/services/apollo-client";
import { useFlags } from "@stadium/services/launch-darkly";
import { useAuth0V2 } from "@stadium/services/auth0";

import { getWorkspaceSlugFromStoreSlug, extractSlugsFromParams } from "@stadium/helpers/store";
import { getWorkspacePermissions, rewritePersonalWorkspacePathInUrl } from "@helpers/workspace";

import { fetchWorkspaceBySlug } from "@graphql/queries/company-space/fetch-workspace";

import useFetchWorkspaces from "@stadium/hooks/lib/use-fetch-workspaces";
import useSelectedWorkspaceSlugCookie from "@stadium/hooks/lib/use-selected-workspace-slug-cookie";

import {
  getPathnameFromUrl,
  getShopUrlWithCurrentWorkspaceSlug as getShopUrlWithCurrentWorkspaceSlugHelper,
} from "@stadium/helpers/routes";
import { matchesUrl } from "@stadium/helpers/dom";

import {
  ORDER_REDEMPTION_PAGE_PATHNAME_WITH_WORKSPACE,
  OLD_ORDER_REDEMPTION_PAGE_PATHNAME_WITH_WORKSPACE,
} from "@constants/routes";
import {
  workspaceRouteUpdateFlag,
  workspacePermissionFlag,
  swagmagicPersonalSpaceLinkFixFlag,
} from "@stadium/services/launch-darkly/constants";
import {
  DEFAULT_WORKSPACE_SLUG,
  MY_WORKSPACE_SLUG,
  WORKSPACE_SLUG_REQUEST_HEADER_KEY,
} from "@stadium/constants";
import { TRIAL_PACKAGE_STATUS } from "@stadium/constants/feature-packages";
import { SHOP_CATALOG_PATH_SUFFIX } from "@stadium/constants/routes";
import { IS_LOCAL_SWAGMAGIC_BUILD, SWAGMAGIC_DOMAIN } from "@constants/env-variables";

type Context = {
  workspaceSlug: string;
  workspaces: any[];
  refetchWorkspaces: () => void;
};

type ProviderProps = {
  children: ReactNode;
  isSwagmagicHost: boolean;
  hostname?: string;
};

export const WorkspaceContext = createContext<Context>({ workspaceSlug: "" });

const AppRouterOverride = ({ children, value }) => {
  const appRouter = useContext(AppRouterContext);
  // server side - app router is null
  if (!appRouter) return children;

  return <AppRouterContext.Provider value={value}>{children}</AppRouterContext.Provider>;
};

export const useWorkspaceContext = () => useContext(WorkspaceContext);

const emptyObj = {};
const emptyArr = [];

const WorkspaceContextProvider: FC<ProviderProps> = ({
  children,
  isSwagmagicHost,
  hostname,
  slug: slugFromProps,
}) => {
  const isSwagmagicDomain = hostname === SWAGMAGIC_DOMAIN || IS_LOCAL_SWAGMAGIC_BUILD;
  const router = useRouter();
  const { replace, pathname, query } = router;
  const { workspaces, refetch: refetchWorkspaces } = useFetchWorkspaces();
  const { isAuthenticated } = useAuth0V2();

  const {
    [workspaceRouteUpdateFlag]: workspaceRouteUpdateFlagEnabled,
    [workspacePermissionFlag]: workspacePermissionFlagEnabled,
    [swagmagicPersonalSpaceLinkFixFlag]: swagmagicPersonalSpaceLinkFixEnabled,
  } = useFlags();

  const {
    slug,
    workspaceSlug: workspaceSlugFromParams,
    isNewRoute,
  } = extractSlugsFromParams(query);
  const orderRedemptionPathname = isNewRoute
    ? ORDER_REDEMPTION_PAGE_PATHNAME_WITH_WORKSPACE
    : OLD_ORDER_REDEMPTION_PAGE_PATHNAME_WITH_WORKSPACE;

  const isOrderRedemptionWithWorkspace = matchesUrl({
    url: pathname,
    pathname: orderRedemptionPathname,
  });

  const [selectedWorkspaceSlugFromCookie, setSelectedWorkspaceSlug] =
    useSelectedWorkspaceSlugCookie();

  const workspaceSlug = useMemo(() => {
    if (slugFromProps) return slugFromProps;
    if (!isSwagmagicHost)
      return getWorkspaceSlugFromStoreSlug(slug, isOrderRedemptionWithWorkspace);
    if (workspaceSlugFromParams) return workspaceSlugFromParams;
    if (selectedWorkspaceSlugFromCookie) return selectedWorkspaceSlugFromCookie;
    const defaultWorkspaceSlug = workspaces.find((workspace) => workspace.isDefault)?.slug;
    if (!defaultWorkspaceSlug && workspaces?.length > 0) return MY_WORKSPACE_SLUG;
    return defaultWorkspaceSlug;
  }, [
    isSwagmagicHost,
    workspaceSlugFromParams,
    selectedWorkspaceSlugFromCookie,
    slug,
    isOrderRedemptionWithWorkspace,
    slugFromProps,
    workspaces,
  ]);

  const isPersonalWorkspace = workspaceSlug === MY_WORKSPACE_SLUG;

  useEffect(() => {
    if (workspaceSlug && isAuthenticated) {
      setSelectedWorkspaceSlug(workspaceSlug);
    }
  }, [workspaceSlug, isAuthenticated, setSelectedWorkspaceSlug]);

  const {
    data: workspaceData,
    loading: workspaceDataLoading,
    error,
  } = useQuery(fetchWorkspaceBySlug, {
    variables: {
      slug: workspaceSlug,
    },
    context: {
      headers: {
        [WORKSPACE_SLUG_REQUEST_HEADER_KEY]: workspaceSlug,
      },
    },
    skip: !workspaceSlug || !workspaceRouteUpdateFlagEnabled || !isAuthenticated || slugFromProps,
  });

  const { workspace, currentWorkspaceUser } = workspaceData || emptyObj;

  const { teams = emptyArr } = currentWorkspaceUser || emptyObj;

  useEffect(() => {
    if (error) {
      const pathname = getPathnameFromUrl(window.location.pathname);
      replace(pathname.replace(workspaceSlug, MY_WORKSPACE_SLUG));
    }
  }, [error]);

  const permissions = useMemo(
    () => getWorkspacePermissions(currentWorkspaceUser, workspacePermissionFlagEnabled),
    [currentWorkspaceUser, workspacePermissionFlagEnabled],
  );

  const teamsWithoutDefault = useMemo(
    () => teams?.filter(({ defaultTeam }) => !defaultTeam),
    [teams],
  );

  const getShopUrlWithCurrentWorkspaceSlug = useCallback(
    (url: string) => getShopUrlWithCurrentWorkspaceSlugHelper(url, workspaceSlug),
    [workspaceSlug],
  );

  const getLockerShopUrlWithCurrentWorkspaceSlug = useCallback(
    (url: string) => {
      const shopUrl = getShopUrlWithCurrentWorkspaceSlugHelper(url, workspaceSlug);
      return `${shopUrl}${SHOP_CATALOG_PATH_SUFFIX}`;
    },
    [workspaceSlug],
  );

  const getDashboardUrlWithCurrentWorkspaceSlug = useCallback(
    (url: string) => {
      const replaceWith = workspaceSlug ? `/${workspaceSlug}` : "";
      return url.replace(`/${DEFAULT_WORKSPACE_SLUG}`, replaceWith);
    },
    [workspaceSlug],
  );

  const { subscription = {} } = workspace || emptyObj;

  const isFreeTrial = subscription?.status === TRIAL_PACKAGE_STATUS;
  const isActivePackageUser = !isFreeTrial && subscription?.daysLeft > 0;

  const shouldFixPersonalWorkspaceUrl = workspaceRouteUpdateFlagEnabled && isSwagmagicDomain;

  const mockRouter = useMemo(() => {
    let mockRouterPathnameOverride = {};
    if (workspaceRouteUpdateFlagEnabled && isSwagmagicDomain) {
      mockRouterPathnameOverride = {
        pathname: router.pathname.replace(/\/w\/\[workspaceSlug](?=\/|$)/, "/s"),
      };
    }
    if (!workspaceRouteUpdateFlagEnabled) {
      mockRouterPathnameOverride = {
        pathname: router.pathname.replace("/[workspaceSlug]", ""),
      };
    }

    const boundPush = router.push.bind(router);
    const boundReplace = router.replace.bind(router);

    return {
      ...router,
      ...mockRouterPathnameOverride,
      push: shouldFixPersonalWorkspaceUrl
        ? (url, options) => boundPush(rewritePersonalWorkspacePathInUrl(url), options)
        : boundPush,
      replace: shouldFixPersonalWorkspaceUrl
        ? (url, options) => boundReplace(rewritePersonalWorkspacePathInUrl(url), options)
        : boundReplace,
    };
  }, [router, workspaceRouteUpdateFlagEnabled, isSwagmagicDomain, shouldFixPersonalWorkspaceUrl]);

  useEffect(() => {
    if (!shouldFixPersonalWorkspaceUrl || !swagmagicPersonalSpaceLinkFixEnabled) return;
    const handlePopState = () => {
      const { pathname, search, hash } = globalThis.location;
      const current = pathname + search;
      const fixed = rewritePersonalWorkspacePathInUrl(current);
      if (fixed !== current) {
        globalThis.history.replaceState(globalThis.history.state, "", fixed + hash);
      }
    };
    globalThis.addEventListener("popstate", handlePopState);
    return () => globalThis.removeEventListener("popstate", handlePopState);
  }, [shouldFixPersonalWorkspaceUrl, swagmagicPersonalSpaceLinkFixEnabled]);

  const value = useMemo(
    () => ({
      workspaceSlug,
      workspaces,
      refetchWorkspaces,
      getShopUrlWithCurrentWorkspaceSlug,
      getDashboardUrlWithCurrentWorkspaceSlug,
      getLockerShopUrlWithCurrentWorkspaceSlug,
      workspaceData,
      workspaceDataLoading,
      currentWorkspaceUser,
      permissions,
      isPersonalWorkspace,
      teamsWithoutDefault,
      teams,
      isActivePackageUser,
      setSelectedWorkspaceSlug,
    }),
    [
      workspaceSlug,
      workspaces,
      refetchWorkspaces,
      getShopUrlWithCurrentWorkspaceSlug,
      getDashboardUrlWithCurrentWorkspaceSlug,
      getLockerShopUrlWithCurrentWorkspaceSlug,
      workspaceData,
      workspaceDataLoading,
      permissions,
      isPersonalWorkspace,
      isActivePackageUser,
      setSelectedWorkspaceSlug,
    ],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      <AppRouterOverride value={mockRouter}>{children}</AppRouterOverride>
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceContextProvider;
